import { faCheck, faInfoCircle, faTimes, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { useToasts, type ToastMessage } from '../../hooks/useToasts';
import styles from '../../hooks/useFlash.module.css';

const AUTO_DISMISS_MS = 5000;
const EXIT_ANIMATION_MS = 200;

function ToastItem({ toast, onDismiss }: { toast: ToastMessage; onDismiss: (id: number) => void }) {
  const [exiting, setExiting] = useState(false);
  const dismissTimer = useRef<number>();
  const exitTimer = useRef<number>();

  useEffect(() => {
    const exit = window.setTimeout(() => setExiting(true), AUTO_DISMISS_MS - EXIT_ANIMATION_MS);
    dismissTimer.current = window.setTimeout(() => onDismiss(toast.id), AUTO_DISMISS_MS);
    return () => {
      window.clearTimeout(exit);
      if (dismissTimer.current) window.clearTimeout(dismissTimer.current);
      if (exitTimer.current) window.clearTimeout(exitTimer.current);
    };
  }, [onDismiss, toast.id]);

  const dismiss = () => {
    if (exiting) return;
    setExiting(true);
    if (dismissTimer.current) window.clearTimeout(dismissTimer.current);
    exitTimer.current = window.setTimeout(() => onDismiss(toast.id), EXIT_ANIMATION_MS);
  };

  return (
    <div
      role={toast.kind === 'error' ? 'alert' : 'status'}
      className={`${styles['flash__item']} ${styles[`flash__${toast.kind}`]} ${exiting ? styles['flash__item--exit'] : ''}`}
      data-testid={toast.kind === 'error' ? 'flash-alert' : 'flash-notice'}
    >
      <span className={styles['flash__icon']}>
        <Icon icon={toast.kind === 'success' ? faCheck : toast.kind === 'error' ? faTriangleExclamation : faInfoCircle} />
      </span>
      <span className={styles['flash__text']}>{toast.message}</span>
      <button type="button" onClick={dismiss} className={styles['flash__btnClose']} aria-label="Cerrar notificación" data-testid="flash-close">
        <Icon icon={faTimes} />
      </button>
    </div>
  );
}

export function ToastContainer() {
  const { toasts, dismiss } = useToasts();
  return (
    <div className={styles['flash__container']} aria-live="polite" aria-atomic="false" data-testid="flash-container">
      {toasts.map((toast) => <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />)}
    </div>
  );
}
